import { CsvRow } from './csvUtils'
import { convertToISOString } from './dateUtils'

export type NotificationType = 'TIME_AFTER_SAVING' | 'SPECIFIC_DATE_AND_TIME'

export type TimeAfterSavingUnit = 'INSTANT' | 'HOUR' | 'DAY' | 'WEEK'

export interface WalletNotification {
  heading?: string
  subHeading?: string
  notificationType: NotificationType
  timeAfterSavingUnit?: TimeAfterSavingUnit
  notificationSendTimeInUTC?: string
}

/**
 * Parses notification columns from a CSV row
 * Supports columns like: Notification1.Type, Notification1.Heading, Notification1.SubHeading, etc.
 */
export const parseNotificationsFromRow = (row: CsvRow): WalletNotification[] => {
  const notifications: WalletNotification[] = []
  const notificationMap = new Map<number, Partial<WalletNotification>>()

  // Find all notification columns in the row
  Object.keys(row).forEach(key => {
    const match = key.match(/^Notification(\d+)\.(.+)$/i)
    if (!match) return

    const notificationIndex = parseInt(match[1], 10)
    const fieldName = match[2].toLowerCase()

    let notification = notificationMap.get(notificationIndex)
    if (!notification) {
      notification = {}
      notificationMap.set(notificationIndex, notification)
    }
    const value = row[key]?.trim()

    switch (fieldName) {
      case 'type':
        if (value) {
          const normalizedType = value.toUpperCase().replace(/[_\s-]/g, '_')
          if (normalizedType === 'TIME_AFTER_SAVING' || normalizedType === 'SPECIFIC_DATE_AND_TIME') {
            notification.notificationType = normalizedType as NotificationType
          }
        }
        break
      case 'heading':
        if (value) {
          notification.heading = value
        }
        break
      case 'subheading':
        if (value) {
          notification.subHeading = value
        }
        break
      case 'timeaftersavingunit':
        if (value) {
          const normalizedUnit = value.toUpperCase()
          if (['INSTANT', 'HOUR', 'DAY', 'WEEK'].includes(normalizedUnit)) {
            notification.timeAfterSavingUnit = normalizedUnit as TimeAfterSavingUnit
          }
        }
        break
      case 'time':
        if (value) {
          // Parse the time and convert to UTC ISO string
          try {
            notification.notificationSendTimeInUTC = convertToISOString(value, false)
          } catch (err) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(`Invalid notification time format: ${value}, skipping notification ${notificationIndex}`)
            }
          }
        }
        break
      default:
        break
    }
  })

  // Convert map to array and validate notifications, maintaining order
  const sortedIndices = Array.from(notificationMap.keys()).sort((a, b) => a - b)

  sortedIndices.forEach(index => {
    const notification = notificationMap.get(index)
    if (!notification) {
      return
    }
    // Only include notifications that have a valid notificationType
    if (notification.notificationType) {
      const validNotification: WalletNotification = {
        notificationType: notification.notificationType,
      }

      // Add optional fields if present
      if (notification.heading) {
        validNotification.heading = notification.heading
      }
      if (notification.subHeading) {
        validNotification.subHeading = notification.subHeading
      }

      // Validate and add type-specific fields
      if (notification.notificationType === 'TIME_AFTER_SAVING') {
        if (notification.timeAfterSavingUnit) {
          validNotification.timeAfterSavingUnit = notification.timeAfterSavingUnit
        } else {
          // Default to INSTANT if not specified
          validNotification.timeAfterSavingUnit = 'INSTANT'
        }
      } else if (notification.notificationType === 'SPECIFIC_DATE_AND_TIME') {
        if (notification.notificationSendTimeInUTC) {
          validNotification.notificationSendTimeInUTC = notification.notificationSendTimeInUTC
        } else {
          // Skip notification if required field is missing
          if (process.env.NODE_ENV === 'development') {
            console.warn(`Notification ${index} has type SPECIFIC_DATE_AND_TIME but no Time field, skipping`)
          }
          return
        }
      }

      notifications.push(validNotification)
    }
  })

  return notifications
}
